/**
 * FAZ 7 - Funnel Intelligence
 * Ana Orchestrator Component
 * 
 * Tüm Funnel Intelligence mantığını yönetir:
 * - Behavior tracking
 * - Segment detection
 * - Micro message display
 * - Telemetri
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBehaviorTracking } from '../../hooks/useBehaviorTracking';
import { MicroMessage } from './MicroMessage';
import './funnel-intelligence.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export const FunnelIntelligence = ({ children }) => {
  const [showMessage, setShowMessage] = useState(false);
  const [userPath, setUserPath] = useState('observer'); // 'observer' | 'deeper' | 'ritual'
  const scrollTriggerRef = useRef(null);
  const telemetrySentRef = useRef(false);
  
  // Tekrar ziyaret kontrolü
  const isReturningUser = localStorage.getItem('faz7_visited');
  
  // Behavior tracking
  const { behavior, segment, trackTextHover, markVisited } = useBehaviorTracking({
    enabled: true,
    onSegmentChange: (newSegment) => {
      console.log('[FAZ7] Segment changed:', newSegment);
      
      // Segment değiştiğinde mesajı göster
      if (newSegment === 'exploring' || newSegment === 'engaged') {
        // Küçük gecikme ile göster (scroll'dan sonra)
        setTimeout(() => setShowMessage(true), 500);
      }
    },
  });
  
  // Scroll trigger için Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && segment && segment !== 'escape') {
            setShowMessage(true);
          }
        });
      },
      { threshold: 0.5 }
    );
    
    if (scrollTriggerRef.current) {
      observer.observe(scrollTriggerRef.current);
    }
    
    return () => observer.disconnect();
  }, [segment]);
  
  // User path detection
  useEffect(() => {
    if (isReturningUser) {
      setUserPath('ritual');
    } else if (behavior.maxScrollDepth >= 60) {
      setUserPath('deeper');
    } else {
      setUserPath('observer');
    }
  }, [isReturningUser, behavior.maxScrollDepth]);
  
  // Telemetri gönder
  const sendTelemetry = useCallback(async () => {
    if (telemetrySentRef.current) return;
    telemetrySentRef.current = true;
    
    try {
      await fetch(`${API_BASE}/api/telemetry/faz7/behavior`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: 'faz7_first_30s',
          segment,
          behavior: {
            timeSpent: behavior.timeSpent,
            maxScrollDepth: behavior.maxScrollDepth,
            focusLost: behavior.focusLost,
            textHoverTime: behavior.textHoverTime,
          },
          userPath,
          isReturningUser: !!isReturningUser,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (error) {
      console.error('[FAZ7] Telemetry error:', error);
    }
  }, [segment, behavior, userPath, isReturningUser]);
  
  // 30 saniye tamamlandığında telemetri gönder
  useEffect(() => {
    if (behavior.trackingComplete && segment) {
      sendTelemetry();
      markVisited();
    }
  }, [behavior.trackingComplete, segment, sendTelemetry, markVisited]);
  
  // Message gösterildiğinde callback
  const handleMessageShow = useCallback((shownSegment) => {
    console.log('[FAZ7] Micro message shown for segment:', shownSegment);
  }, []);
  
  return (
    <div className="funnel-intelligence">
      {/* Children (asıl sayfa içeriği) */}
      {children}
      
      {/* Scroll Trigger Point - Hero'dan sonra */}
      <div 
        ref={scrollTriggerRef} 
        className="funnel-intelligence__trigger"
        aria-hidden="true"
      />
      
      {/* Micro Message - Sabit pozisyonda */}
      <div className="funnel-intelligence__message-container">
        <MicroMessage
          segment={segment}
          visible={showMessage}
          variant={userPath === 'ritual' ? 'ritual' : 'default'}
          onShow={handleMessageShow}
        />
      </div>
      
      {/* Debug panel removed for production */}
    </div>
  );
};

export default FunnelIntelligence;
